import { immerable } from 'immer';
import { indexBy, omit, pick, uniq, without, zipObj } from 'ramda';

export class EntityContainer<T, K extends string = string> {
    [immerable] = true;

    public byId: { [key: string]: T };
    public allIds: Array<K>;
    public items: T[];

    constructor(container?: { byId: { [key: string]: T }; allIds: Array<K> }) {
        if (container) {
            this.byId = { ...container.byId };
            this.allIds = container.allIds.slice();
            this.items = Object.values(container.byId);
        } else {
            this.byId = {};
            this.allIds = [];
            this.items = [];
        }
    }

    public slice(start = 0, offset = 1): EntityContainer<T, K> {
        return new EntityContainer({
            byId: pick(this.allIds.slice(start, offset), this.byId),
            allIds: this.allIds.slice(start, offset),
        });
    }

    public sort(comparator: (a: T, b: T) => number): EntityContainer<T, K> {
        return new EntityContainer({
            byId: this.byId,
            allIds: this.allIds.slice().sort((a, b) => comparator(this.byId[a], this.byId[b])),
        });
    }

    public filter(condition: (a: T) => boolean): EntityContainer<T, K> {
        const excludables = this.allIds.filter(id => !condition(this.byId[id]));
        return this.exclude(...excludables);
    }

    public combine(entities: EntityContainer<T, K>): EntityContainer<T, K> {
        return new EntityContainer({
            byId: { ...this.byId, ...entities.byId },
            allIds: uniq(this.allIds.concat(entities.allIds)),
        });
    }

    public update(key: K, item: Partial<T>): EntityContainer<T, K> {
        if (!this.byId[key]) {
            return this;
        }

        return new EntityContainer({
            byId: { ...this.byId, [key]: { ...this.byId[key], ...item } },
            allIds: this.allIds,
        });
    }

    public append(key: K, newEntity: T): EntityContainer<T, K> {
        return new EntityContainer({
            byId: { ...this.byId, [key]: newEntity },
            allIds: uniq(this.allIds.concat(key)),
        });
    }

    public appendAll(keyer: (el: T) => K, newEntities: T[]): EntityContainer<T, K> {
        return new EntityContainer({
            byId: { ...this.byId, ...indexBy(keyer, newEntities) },
            allIds: uniq([...this.allIds, ...newEntities.map(keyer)]),
        });
    }

    public prepend(key: K, newEntity: T): EntityContainer<T, K> {
        return new EntityContainer({
            byId: { ...this.byId, [key]: newEntity },
            allIds: uniq([key, ...this.allIds]),
        });
    }

    public prependAll(keyer: (el: T) => K, newEntities: T[]): EntityContainer<T, K> {
        return new EntityContainer({
            byId: { ...this.byId, ...indexBy(keyer, newEntities) },
            allIds: uniq([...newEntities.map(keyer), ...this.allIds]),
        });
    }

    public exclude(...keys: K[]): EntityContainer<T, K> {
        return new EntityContainer({
            byId: omit(keys, this.byId),
            allIds: without(keys, this.allIds),
        });
    }

    public contains(predicate: (el: T) => boolean): boolean {
        for (const key of this.allIds) {
            try {
                if (predicate(this.byId[key])) {
                    return true;
                }
            } catch (_error) {
                continue;
            }
        }

        return false;
    }

    public copy(): EntityContainer<T, K> {
        return new EntityContainer({
            byId: { ...this.byId },
            allIds: this.allIds.slice(),
        });
    }

    public asList(): T[] {
        return this.allIds.map(id => this.byId[id]);
    }

    public static fromArray<T>(entities: T[], keyer: (element: T) => string): EntityContainer<T> {
        return new EntityContainer({
            byId: zipObj(entities.map(keyer), entities),
            allIds: entities.map(keyer),
        });
    }
}
